/**
 * Status as a chip: a dot, a word or figure, a tint.
 *
 * Colour is never the only carrier. The text is always present and the three
 * status tones also draw a dot, so the chip reads in grayscale, in forced
 * colours, and to a reader who does not see red and green apart. The tone is
 * exposed as `data-tone` so tests assert meaning, never a colour class.
 *
 * This component only draws. Deciding the tone is the caller's job
 * (MarginBadge, RiskBasisBadge, ...), which keeps every threshold where its
 * reasoning lives.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type ChipTone = "good" | "warn" | "bad" | "info" | "neutral";

const TONE_CLASS: Record<ChipTone, string> = {
  good: "bg-[var(--success-bg)] text-[var(--success)]",
  warn: "bg-[var(--warning-bg)] text-[var(--warning)]",
  bad: "bg-[var(--danger-bg)] text-[var(--danger)]",
  info: "bg-[var(--info-bg)] text-[var(--info)]",
  neutral: "bg-muted text-muted-foreground",
};

const DOT_BY_DEFAULT: Record<ChipTone, boolean> = {
  good: true,
  warn: true,
  bad: true,
  info: false,
  neutral: false,
};

export function StatusChip({
  tone,
  dot,
  title,
  className,
  children,
}: {
  tone: ChipTone;
  /** Defaults on for good/warn/bad, off for info/neutral. */
  dot?: boolean;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  const showDot = dot ?? DOT_BY_DEFAULT[tone];
  return (
    <span
      data-slot="status-chip"
      data-tone={tone}
      title={title}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 font-display text-[11.5px] font-semibold tabular-nums",
        TONE_CLASS[tone],
        className,
      )}
    >
      {showDot ? (
        <span data-chip-dot aria-hidden className="size-[7px] rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}
